import React, { useState } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, 
  AlertTriangle, 
  Trash2,
  DollarSign,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CancelJobModal = ({ job, isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');

  const handleCancel = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/jobs/cancel`, {
        job_id: job.id,
        reason: reason.trim() || null
      });
      
      // Handle different response scenarios
      if (response.data.admin_alert) {
        // Refund failed - admin only alert
        toast.error(`Job cancelled but refund failed: ${response.data.refund_error}`);
      } else if (response.data.refund_amount > 0) {
        // Successful refund
        toast.success(`Job cancelled and $${response.data.refund_amount} refunded successfully`);
      } else {
        // Successful cancellation without refund
        toast.success('Job cancelled successfully');
      }
      
      if (response.data.notifications_sent > 0) {
        toast.info(`${response.data.notifications_sent} receivers notified of job withdrawal`);
      }
      
      onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Failed to cancel job:', error);
      const message = error.response?.data?.detail || 'Failed to cancel job';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !job) return null;

  const isFunded = job.payment_status === 'funded';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                Cancel Job
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                This action will permanently cancel the job
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Job Information */}
          <div className="mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">{job.title}</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Location: {job.location}</div>
                <div>Quote: ${job.quoted_price}</div>
                <div>Status: {job.status}</div>
                {isFunded && (
                  <div className="text-green-700 font-medium">
                    ✓ Funded (${job.originator_amount || job.quoted_price} escrowed)
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Refund Warning */}
          {isFunded && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <DollarSign className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Automatic Refund</h4>
                  <p className="text-sm text-blue-700">
                    This job is funded. Cancelling will automatically refund ${job.originator_amount || job.quoted_price} 
                    {job.payment_intent_id?.startsWith('test_') ? ' (mock refund)' : ' to your payment method'}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cancellation Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Cancellation (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows="3"
              className="jop-input resize-none"
              placeholder="e.g., Job requirements changed, no longer needed..."
            />
          </div>

          {/* Confirmation Warning */}
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
              <span className="text-sm text-red-700 font-medium">
                This action cannot be undone. The job will be permanently cancelled.
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="jop-button flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {isFunded ? 'Cancel & Refund Job' : 'Cancel Job'}
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="jop-button-secondary"
              disabled={loading}
            >
              Keep Job
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelJobModal;